/**
 * SecuredCollectToggle – "Porte de collecte Mutualisée" option on an FTTO order.
 *
 * Off  : a single "Référence porte de collecte" select, current behaviour.
 * On   : the select becomes "Référence porte de collecte primaire" and a
 *        "Référence porte de collecte backup" select appears. Both PDC of a
 *        mutualized set are selectable, in either role.
 *
 * Pair validation is driven by the mutualization map rendered on the checkbox
 * (data-mutualization-map): { "<pdc value>": "<mutualized peer value>"|null }.
 *   - same PDC twice, or a PDC already mutualized with a third one → blocking error
 *   - two PDC not mutualized yet                                   → warning, order allowed
 *     (the backend alerts the engineering team so the ressources get mutualized)
 *
 * Usage:
 *   this.securedCollect = new SecuredCollectToggle();
 *   this.securedCollect.destroy();
 */
import Swal from 'sweetalert2';

const NS = '.securedCollect';

export default class SecuredCollectToggle {
  constructor({ onChange = null } = {}) {
    this.$toggle = $('.js-secured-collect-toggle');
    this.$primarySelect = $('.js-pdc-select');
    this.$backupSelect = $('.js-backup-pdc-select');
    this.$backupGroup = $('.js-backup-pdc-group');
    this.$error = $('.js-secured-collect-error');
    this.onChange = onChange;

    this._warnedPair = null; // pair the "not mutualized yet" warning was shown for

    this.map = this._readMap();

    if (this.$toggle.length === 0 || this.$backupSelect.length === 0) return;

    this._bindEvents();
    this._applyMode();
  }

  /** True when the option is on and the selected pair is usable. */
  get isValid() {
    if (!this._isSecured()) return true;

    const primary = this.$primarySelect.val();
    const backup = this.$backupSelect.val();
    if (!primary || !backup) return false;

    return this._pairError(primary, backup) === null;
  }

  destroy() {
    this.$toggle.off(`change${NS}`);
    this.$primarySelect.off(`change${NS}`);
    this.$backupSelect.off(`change${NS}`);
  }

  // ── Private ───────────────────────────────────────────────────────────────

  _readMap() {
    const raw = this.$toggle.data('mutualization-map');
    if (!raw) return {};
    if (typeof raw === 'object') return raw;

    try {
      return JSON.parse(raw);
    } catch (e) {
      return {};
    }
  }

  _bindEvents() {
    this.$toggle.on(`change${NS}`, () => this._applyMode());
    this.$primarySelect.on(`change${NS}`, () => this._validatePair());
    this.$backupSelect.on(`change${NS}`, () => this._validatePair());
  }

  _isSecured() {
    return this.$toggle.is(':checked');
  }

  _applyMode() {
    const secured = this._isSecured();

    this.$backupGroup.toggle(secured);
    this.$backupSelect.prop('required', secured);
    if (!secured) this.$backupSelect.val('');

    this._toggleSecuredOnlyOptions(secured);
    this._swapLabels(secured);
    this._clearError();
    this._warnedPair = null;

    this._syncSummary();
    if (secured) this._validatePair();
    if (typeof this.onChange === 'function') this.onChange(secured);
  }

  /**
   * The SECONDARY PDC of a mutualized set is only a valid target for a secured
   * service: keep it out of the primary select when the option is off.
   */
  _toggleSecuredOnlyOptions(secured) {
    const $securedOnly = this.$primarySelect.find('option[data-secured-only="true"]');
    if ($securedOnly.length === 0) return;

    if (!secured && $securedOnly.filter(':selected').length > 0) {
      this.$primarySelect.val('').trigger('change');
    }
    $securedOnly.prop('disabled', !secured).toggle(secured);
  }

  _swapLabels(secured) {
    $('.js-pdc-primary-label, .js-summary-pdc-label').each((_index, element) => {
      const $label = $(element);
      const text = secured ? $label.data('label-secured') : $label.data('label-standard');
      if (text) $label.text(text);
    });
  }

  _validatePair() {
    this._syncSummary();
    if (!this._isSecured()) return;

    const primary = this.$primarySelect.val();
    const backup = this.$backupSelect.val();
    if (!primary || !backup) {
      this._clearError();
      return;
    }

    const error = this._pairError(primary, backup);
    if (error) {
      this._showError(error);
      return;
    }

    this._clearError();
    if (!this._isMutualizedPair(primary, backup)) this._warnPendingMutualization(primary, backup);
  }

  _isMutualizedPair(primary, backup) {
    return this.map[primary] === backup && this.map[backup] === primary;
  }

  /** Blocking error for the selected pair, or null when it is acceptable. */
  _pairError(primary, backup) {
    if (primary === backup) {
      return 'La porte de collecte backup doit être différente de la porte de collecte primaire.';
    }
    if (this._isMutualizedPair(primary, backup)) return null;

    const primaryPeer = this.map[primary];
    const backupPeer = this.map[backup];
    if (primaryPeer || backupPeer) {
      return 'Ces deux portes de collecte appartiennent déjà à des ensembles de mutualisation '
        + 'différents. Sélectionnez les deux portes d’un même ensemble mutualisé.';
    }

    return null; // two standalone PDC: allowed, engineering will mutualize them
  }

  /** CA4: order allowed, but the client is told the PDC aren't mutualized yet. */
  _warnPendingMutualization(primary, backup) {
    const pair = `${primary}|${backup}`;
    if (this._warnedPair === pair) return;
    this._warnedPair = pair;

    Swal.fire({
      title: '<i class="mdi mdi-alert-outline text-warning mr-1"></i> Portes de collecte non mutualisées',
      html: `Les portes de collecte <strong>${primary}</strong> et <strong>${backup}</strong> ne sont pas `
        + 'encore sécurisées / mutualisées.<br>La prise de commande entraînera une évolution de la '
        + 'mutualisation de ces portes de collecte : nos équipes Ingénierie sont alertées pour '
        + 'réaliser la modification au niveau des ressources.',
      confirmButtonText: 'J’ai compris',
      buttonsStyling: false,
      customClass: { confirmButton: 'btn btn-success mt-2' }
    });
  }

  /** Keep the extranet summary tab in sync with the secured selection. */
  _syncSummary() {
    const $row = $('.js-summary-backup-pdc-row');
    if ($row.length === 0) return;

    const secured = this._isSecured();
    $row.toggle(secured);
    $('#summary_backup_pdc_order_id').text(secured ? (this.$backupSelect.val() || '--') : '--');
  }

  _showError(message) {
    this.$backupSelect.addClass('is-invalid');
    this.$error.text(message).show();
  }

  _clearError() {
    this.$backupSelect.removeClass('is-invalid');
    this.$error.hide();
  }
}
